import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const getSeverityStyles = (severity) => {
    switch (severity) {
        case 'critical':
            return 'severity-critical';
        case 'high':
            return 'severity-high';
        case 'medium':
            return 'severity-medium';
        default:
            return 'severity-low';
    }
};

const getSeverityIcon = (severity) => {
    if (severity === 'critical' || severity === 'high') {
        return <AlertTriangle className="h-3 w-3" />;
    }
    return null;
};

export const AnomalyTable = ({ anomalies, title = "Recent Anomalies", showViewAll = true, limit = 5 }) => {
    const navigate = useNavigate();
    const displayAnomalies = anomalies?.slice(0, limit) || [];

    return (
        <Card className="bg-card border-border" data-testid="anomaly-table">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg font-semibold text-card-foreground flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    {title}
                </CardTitle>
                {showViewAll && anomalies?.length > limit && (
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate('/anomalies')}
                        className="text-primary"
                        data-testid="view-all-anomalies"
                    >
                        View All
                        <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                {displayAnomalies.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No anomalies detected</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {displayAnomalies.map((anomaly) => (
                            <div
                                key={anomaly.id}
                                className={cn(
                                    "flex items-center justify-between p-3 rounded-lg",
                                    "bg-background/50 border border-border",
                                    "hover:border-primary/30 cursor-pointer transition-colors duration-200"
                                )}
                                onClick={() => navigate(`/anomalies/${anomaly.id}`)}
                                data-testid={`anomaly-row-${anomaly.id}`}
                            >
                                <div className="flex items-center gap-4">
                                    <Badge 
                                        variant="outline" 
                                        className={cn("text-xs border", getSeverityStyles(anomaly.severity))}
                                    >
                                        {getSeverityIcon(anomaly.severity)}
                                        <span className="ml-1 capitalize">{anomaly.severity}</span>
                                    </Badge>
                                    <div>
                                        <p className="font-medium text-card-foreground">{anomaly.kpi_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {format(new Date(anomaly.date), 'MMM d, yyyy')}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono text-sm text-card-foreground">
                                        {anomaly.value.toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        z-score: <span className={cn(
                                            "font-mono",
                                            Math.abs(anomaly.z_score) >= 3 ? "text-destructive" : "text-orange-400"
                                        )}>{anomaly.z_score.toFixed(2)}</span>
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default AnomalyTable;
